import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import {
  ShiftRequest,
  ShiftRequestsResponse,
  ShiftRequestTypesResponse,
  CreateShiftRequestPayload,
} from 'types/shift-requests';

export type ShiftRequestEndpointType =
  | 'availability'
  | 'time-off'
  | 'cover'
  | 'pickup'
  | 'trade';

const SHIFT_REQUEST_TYPE_PATHS: Record<ShiftRequestEndpointType, string> = {
  availability: 'availability',
  'time-off': 'time-off',
  cover: 'cover',
  pickup: 'pickup',
  trade: 'trade',
};

export const resolveShiftRequestEndpointType = (
  requestType?: string | null
): ShiftRequestEndpointType | null => {
  if (!requestType) return null;

  const typeLower = requestType.toLowerCase();
  if (typeLower === 'availability' || typeLower.includes('availability')) return 'availability';
  if (
    typeLower === 'time-off' ||
    typeLower.includes('time off') ||
    typeLower.includes('time-off')
  ) {
    return 'time-off';
  }
  if (typeLower === 'cover' || typeLower.includes('cover')) return 'cover';
  if (
    typeLower === 'pickup' ||
    typeLower.includes('pickup') ||
    typeLower.includes('pick up')
  ) {
    return 'pickup';
  }
  if (typeLower === 'trade' || typeLower.includes('trade')) return 'trade';

  return null;
};

export const getShiftRequestCollectionEndpoint = (
  requestType?: string | null
): string => {
  const type = resolveShiftRequestEndpointType(requestType);
  if (!type) return 'shift_requests';
  return `shift_requests/${SHIFT_REQUEST_TYPE_PATHS[type]}`;
};

export const getShiftRequestItemEndpoint = (
  requestId: number | string,
  requestType?: string | null
): string => `${getShiftRequestCollectionEndpoint(requestType)}/${requestId}`;

export const getShiftRequestDeleteEndpoint = (
  requestId: number | string
): string => `shift_requests/${requestId}`;

// Get all shift requests
export const getShiftRequests = async (params?: {
  workplace_id?: number;
  employee_id?: number;
  fromDate?: string;
  toDate?: string;
  page?: number;
  limit?: number;
}): Promise<ShiftRequestsResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.workplace_id) queryParams.append('workplace', params.workplace_id.toString());
  if (params?.employee_id) queryParams.append('employeeRequest', params.employee_id.toString());
  if (params?.fromDate) queryParams.append('startAt[after]', params.fromDate);
  if (params?.toDate) queryParams.append('startAt[before]', params.toDate);

  const endpoint = queryParams.toString() 
    ? `shift_requests?${queryParams.toString()}`
    : 'shift_requests';

  const itemsPerPage = params?.limit || 30;
  const data = await paginationService.getWithHandler(endpoint, undefined, itemsPerPage);
  return data;
};

// Get single shift request by ID
export const getShiftRequest = async (requestId: number): Promise<ShiftRequest> => {
  const data = apiService.responseHandler(
    await apiService.get(`shift_requests/${requestId}`)
  );
  return data;
};

// Create a new shift request
export const createShiftRequest = async (
  payload: CreateShiftRequestPayload
): Promise<ShiftRequest> => {
  const { requestType, ...apiPayload } = payload;
  const endpoint = getShiftRequestCollectionEndpoint(requestType);
  
  const data = apiService.responseHandler(
    await apiService.post(endpoint, apiPayload)
  );
  return data;
};

// Update a shift request using the same type-specific path as POST, plus the id
export const updateShiftRequest = async (
  requestId: number,
  payload: Partial<CreateShiftRequestPayload>
): Promise<ShiftRequest> => {
  const { requestType, ...apiPayload } = payload;
  const endpoint = getShiftRequestItemEndpoint(requestId, requestType);

  const data = apiService.responseHandler(
    await apiService.put(endpoint, apiPayload)
  );
  return data;
};

// Delete a shift request by id. Type is not part of the DELETE path.
export const deleteShiftRequest = async (requestId: number): Promise<void> => {
  await apiService.delete(getShiftRequestDeleteEndpoint(requestId));
};

// Approve or deny a shift request using type-specific endpoints
export const approveOrDenyShiftRequest = async (
  requestId: number,
  requestType: string,
  approve: boolean
): Promise<ShiftRequest> => {
  const payload = {
    employeeRequestConfirmed: approve ? true : false
  };
  const endpoint = getShiftRequestItemEndpoint(requestId, requestType);
  
  const data = apiService.responseHandler(
    await apiService.put(endpoint, payload)
  );
  return data;
};

// Get all shift request types
export const getShiftRequestTypes = async (): Promise<ShiftRequestTypesResponse> => {
  const data = await paginationService.getWithHandler('shift_request_types');
  const types = data?.member || data?.['hydra:member'] || [];
  
  return {
    '@context': data['@context'] || '/api/contexts/ShiftRequestType',
    '@id': data['@id'] || '/api/shift_request_types',
    '@type': data['@type'] || 'Collection',
    'totalItems': data.totalItems || types.length,
    'member': types
  };
};
